/**
 * NeoCoreTestCobranzaPage — test de recuperación de mora (rec-oct-2026).
 *
 * 90 créditos con >7 días hábiles de atraso al 15-oct, sorteados en 3 grupos:
 *   A · control: solo la lista diaria (no se llama).
 *   B · llamada + WhatsApp, sin oferta.
 *   C · llamada + WhatsApp + condonación del 100% del recargo si paga la cuota
 *       completa dentro de 5 días hábiles desde el primer contacto.
 *
 * Natalie llama, registra cada contacto y (solo en C, cuando el asociado paga)
 * condona el recargo ANTES de registrar el pago. El resultado se mide solo.
 *
 * Backend: cobranza_test_listar · cobranza_test_registrar_contacto ·
 *          cobranza_test_resultados · condonar_mora_credito (migración 0364).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowsClockwise, Phone, WhatsappLogo, HandHeart, NotePencil } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { ReferenciasCobranza } from '@/components/ReferenciasCobranza'
import { useIsMobile } from '@/hooks/use-mobile'

const TEST_ID = 'rec-oct-2026'

type Row = {
  credito_id: string
  grupo: 'A' | 'B' | 'C'
  estrato: string
  nombre: string
  primer_nombre: string
  no_asociado: string
  telefono: string | null
  dias_inicio: number
  dias_hoy: number
  vencido_inicio: number
  mora_inicio: number
  vencido_hoy: number | null
  mora_hoy: number | null
  estado: 'en_mora' | 'pago_parcial' | 'al_dia'
  pagado_desde_inicio: number
  ultimo_resultado: string | null
  ultimo_contacto: string | null
  contactos: number
  fecha_limite_oferta: string | null
  puede_condonar: boolean
  motivo_no_condonar: string | null
}

type Res = {
  grupo: string; creditos: number; contactados: number; prometieron: number
  al_dia: number; pago_parcial: number; pct_al_dia: number; recuperado: number; condonado: number
}

const RESULTADOS: { v: string; l: string }[] = [
  { v: 'no_contesta', l: 'No contestó' },
  { v: 'numero_malo', l: 'Número equivocado / apagado' },
  { v: 'contesto', l: 'Contestó, sin compromiso' },
  { v: 'prometio_pagar', l: 'Prometió pagar' },
  { v: 'no_puede_pagar', l: 'No puede pagar ahora' },
  { v: 'rechazo', l: 'Se negó' },
  { v: 'pago', l: 'Pagó' },
]
const LABEL_RES = Object.fromEntries(RESULTADOS.map((r) => [r.v, r.l]))

const fmtQ = (n: number | null | undefined) =>
  'Q' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// Suma n días hábiles (lunes a sábado), igual que fecha_habil_n en la base.
function sumarHabiles(desde: Date, n: number): Date {
  const d = new Date(desde)
  let k = 0
  while (k < n) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0) k++ }
  return d
}
const ddmm = (d: Date) => d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', timeZone: 'America/Guatemala' })

function mensaje(r: Row): string {
  const cuota = Math.max(0, Number(r.vencido_hoy ?? 0) - Number(r.mora_hoy ?? 0))
  if (r.grupo === 'C') {
    const lim = r.fecha_limite_oferta ? ddmm(new Date(r.fecha_limite_oferta + 'T12:00:00-06:00')) : ddmm(sumarHabiles(new Date(), 5))
    return `Hola ${r.primer_nombre}, soy Natalie de Neocoop. Queremos ayudarte a ponerte al día: si pagás tu cuota de ${fmtQ(cuota)} a más tardar el ${lim}, te perdonamos el recargo por mora de ${fmtQ(r.mora_hoy)}. Podés pagar desde la app o con depósito, y me avisás por acá. ¿Te parece?`
  }
  return `Hola ${r.primer_nombre}, soy Natalie de Neocoop. Te escribo porque tenés un saldo pendiente de ${fmtQ(r.vencido_hoy)} en tu crédito. ¿Cómo estás? Si estás pasando por algo, contame y lo vemos juntos. Podés pagar desde la app o con depósito.`
}
const waLink = (tel: string | null, txt: string) => {
  const num = (tel ?? '').replace(/\D/g, '')
  return num.length >= 8 ? `https://wa.me/${num}?text=${encodeURIComponent(txt)}` : null
}

const ESTADO: Record<Row['estado'], { l: string; bg: string; fg: string }> = {
  en_mora: { l: 'En mora', bg: 'rgba(220,38,38,.08)', fg: '#b91c1c' },
  pago_parcial: { l: 'Abonó', bg: 'rgba(234,179,8,.12)', fg: '#92400e' },
  al_dia: { l: 'Al día', bg: 'rgba(22,163,74,.10)', fg: '#15803d' },
}

export function NeoCoreTestCobranzaPage() {
  const [rows, setRows] = useState<Row[]>([])
  const [res, setRes] = useState<Res[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'B' | 'C' | 'A'>('B')
  const [form, setForm] = useState<{ id: string; canal: string; resultado: string; promesa: string; notas: string } | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const [l, r] = await Promise.all([
      supabase.rpc('cobranza_test_listar', { p_test: TEST_ID }),
      supabase.rpc('cobranza_test_resultados', { p_test: TEST_ID }),
    ])
    if (l.error) alert('Error: ' + l.error.message)
    setRows((l.data as Row[]) ?? [])
    setRes((r.data as Res[]) ?? [])
    setLoading(false)
  }, [])
  useEffect(() => { void load() }, [load])

  const lista = useMemo(() => rows.filter((r) => r.grupo === tab), [rows, tab])

  const guardar = async () => {
    if (!form) return
    setSaving(true)
    const { error } = await supabase.rpc('cobranza_test_registrar_contacto', {
      p_credito_id: form.id, p_canal: form.canal, p_resultado: form.resultado,
      p_fecha_promesa: form.promesa || null, p_notas: form.notas || null, p_test: TEST_ID,
    })
    setSaving(false)
    if (error) { alert('Error: ' + error.message); return }
    setForm(null)
    void load()
  }

  const condonar = async (r: Row) => {
    if (!r.puede_condonar) { alert(r.motivo_no_condonar ?? 'No se puede condonar.'); return }
    const ok = confirm(
      `¿Condonar el 100% del recargo (${fmtQ(r.mora_hoy)}) de ${r.nombre}?\n\n` +
      'Hacelo SOLO si el asociado ya pagó o está pagando la cuota completa dentro del plazo. ' +
      'Condoná ANTES de registrar el pago, para que el pago vaya a capital e interés.')
    if (!ok) return
    const { error } = await supabase.rpc('cobranza_test_condonar', { p_credito_id: r.credito_id, p_test: TEST_ID })
    if (error) { alert('Error: ' + error.message); return }
    void load()
  }

  const tabs: { k: 'B' | 'C' | 'A'; l: string; d: string }[] = [
    { k: 'B', l: 'Grupo B · Llamada', d: 'Llamar y escribir por WhatsApp. Sin oferta.' },
    { k: 'C', l: 'Grupo C · Llamada + oferta', d: 'Llamar y ofrecer: si paga la cuota completa en 5 días hábiles, se le perdona el recargo.' },
    { k: 'A', l: 'Grupo A · Control', d: 'NO llamar. Solo lo que ya llega en la lista diaria. Sirve para comparar.' },
  ]

  const isMobile = useIsMobile()
  const tabActual = tabs.find((t) => t.k === tab)

  // Acciones + formulario de registro (iguales en tabla y en tarjetas).
  const acciones = (r: Row, grande: boolean) => {
    const wa = waLink(r.telefono, mensaje(r))
    const btn: React.CSSProperties = grande
      ? { flex: 1, justifyContent: 'center', minHeight: 40, display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)' }
      : {}
    return (
      <>
        {tab !== 'A' && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {r.telefono && <a className="btn btn-ghost" style={btn} href={`tel:${r.telefono}`} title="Llamar"><Phone size={grande ? 16 : 14} />{grande && ' Llamar'}</a>}
            {wa && <a className="btn btn-ghost" style={btn} href={wa} target="_blank" rel="noreferrer" title="WhatsApp con mensaje listo"><WhatsappLogo size={grande ? 16 : 14} />{grande && ' WhatsApp'}</a>}
            <button className="btn btn-ghost" style={btn} title="Registrar contacto"
              onClick={() => setForm({ id: r.credito_id, canal: 'llamada', resultado: 'no_contesta', promesa: '', notas: '' })}>
              <NotePencil size={grande ? 16 : 14} /> Registrar
            </button>
            {tab === 'C' && r.estado !== 'al_dia' && Number(r.mora_hoy) > 0 && (
              <button className="btn btn-ghost" disabled={!r.puede_condonar}
                title={r.puede_condonar ? 'Condonar recargo (solo si paga la cuota completa en plazo)' : (r.motivo_no_condonar ?? '')}
                style={{ ...btn, opacity: r.puede_condonar ? 1 : 0.45 }} onClick={() => void condonar(r)}>
                <HandHeart size={grande ? 16 : 14} /> Condonar
              </button>
            )}
          </div>
        )}
        {form?.id === r.credito_id && (
          <div style={{ marginTop: 10, display: 'grid', gap: 8, minWidth: grande ? 0 : 260 }}>
            <select value={form.canal} onChange={(ev) => setForm({ ...form, canal: ev.target.value })} style={grande ? { minHeight: 40, fontSize: 15 } : undefined}>
              <option value="llamada">Llamada</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="sms">SMS</option>
            </select>
            <select value={form.resultado} onChange={(ev) => setForm({ ...form, resultado: ev.target.value })} style={grande ? { minHeight: 40, fontSize: 15 } : undefined}>
              {RESULTADOS.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
            </select>
            {form.resultado === 'prometio_pagar' && (
              <label style={{ fontSize: 12, display: 'grid', gap: 4 }}>Fecha prometida <input type="date" value={form.promesa} onChange={(ev) => setForm({ ...form, promesa: ev.target.value })} style={grande ? { minHeight: 40, fontSize: 15 } : undefined} /></label>
            )}
            <input placeholder="Notas (opcional)" value={form.notas} onChange={(ev) => setForm({ ...form, notas: ev.target.value })} style={grande ? { minHeight: 40, fontSize: 15 } : undefined} />
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-primary" style={grande ? { flex: 1, minHeight: 42 } : undefined} disabled={saving} onClick={() => void guardar()}>{saving ? 'Guardando…' : 'Guardar'}</button>
              <button className="btn btn-ghost" style={grande ? { flex: 1, minHeight: 42 } : undefined} onClick={() => setForm(null)}>Cancelar</button>
            </div>
          </div>
        )}
      </>
    )
  }

  const ultimoContacto = (r: Row) => r.ultimo_resultado
    ? <>{LABEL_RES[r.ultimo_resultado] ?? r.ultimo_resultado}<span style={{ color: 'var(--muted-foreground)' }}>
        {' · '}{new Date(r.ultimo_contacto!).toLocaleString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/Guatemala' })} · {r.contactos} contacto(s)</span></>
    : <span style={{ color: 'var(--muted-foreground)' }}>Sin contacto registrado</span>

  return (
    <div style={{ padding: isMobile ? '14px 12px' : '20px 24px', maxWidth: 1350, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'flex-end', justifyContent: 'space-between', marginBottom: 14, gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: isMobile ? 19 : 22, fontWeight: 800, letterSpacing: '-0.01em' }}>Test de cobranza · octubre</h1>
          <p style={{ margin: '4px 0 0', fontSize: isMobile ? 12.5 : 13, color: 'var(--muted-foreground)', maxWidth: 820 }}>
            {isMobile
              ? '90 créditos, 3 grupos · 21-oct al 4-nov. Registrá cada llamada o mensaje.'
              : '90 créditos con más de 7 días hábiles de atraso, sorteados en 3 grupos. Del 21-oct al 4-nov. Registrá cada llamada o mensaje: sin registro no sabemos qué funcionó.'}
          </p>
        </div>
        <button onClick={() => void load()} className="btn btn-ghost" title="Refrescar"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, border: isMobile ? '1px solid var(--border)' : undefined }}>
          <ArrowsClockwise size={14} weight="bold" />{!isMobile && ' Refrescar'}
        </button>
      </div>

      {/* Resultados por grupo: 3 columnas en escritorio, filas compactas en celular */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? 8 : 12, marginBottom: 16 }}>
        {(['A', 'B', 'C'] as const).map((g) => {
          const x = res.find((r) => r.grupo === g)
          return isMobile ? (
            <div key={g} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--card)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 24, fontWeight: 800, minWidth: 74, fontVariantNumeric: 'tabular-nums' }}>{Number(x?.pct_al_dia ?? 0).toFixed(1)}%</div>
              <div style={{ minWidth: 0, fontSize: 12 }}>
                <div style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', fontSize: 11, color: 'var(--muted-foreground)' }}>
                  Grupo {g} · {x?.creditos ?? 0} créditos
                </div>
                <div style={{ color: 'var(--muted-foreground)' }}>
                  al día {x?.al_dia ?? 0} · abonaron {x?.pago_parcial ?? 0} · contactados {x?.contactados ?? 0} · prometieron {x?.prometieron ?? 0}
                </div>
                <div>Recuperado <b>{fmtQ(x?.recuperado)}</b>{g === 'C' ? <> · condonado <b>{fmtQ(x?.condonado)}</b></> : null}</div>
              </div>
            </div>
          ) : (
            <div key={g} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', background: 'var(--card)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--muted-foreground)' }}>
                Grupo {g} · {x?.creditos ?? 0} créditos
              </div>
              <div style={{ fontSize: 30, fontWeight: 800, marginTop: 4 }}>{Number(x?.pct_al_dia ?? 0).toFixed(1)}%</div>
              <div style={{ fontSize: 12.5, color: 'var(--muted-foreground)' }}>
                al día ({x?.al_dia ?? 0}) · abonaron {x?.pago_parcial ?? 0} · contactados {x?.contactados ?? 0} · prometieron {x?.prometieron ?? 0}
              </div>
              <div style={{ fontSize: 12.5, marginTop: 4 }}>
                Recuperado <b>{fmtQ(x?.recuperado)}</b>{g === 'C' ? <> · condonado <b>{fmtQ(x?.condonado)}</b></> : null}
              </div>
            </div>
          )
        })}
      </div>

      {/* Tabs: en celular, segmentado de 3 que entra en pantalla */}
      {isMobile ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, padding: 4, background: 'var(--muted, #f4f4f5)', borderRadius: 10, marginBottom: 10 }}>
          {tabs.map((t) => (
            <button key={t.k} onClick={() => { setTab(t.k); setForm(null) }}
              style={{ border: 0, borderRadius: 8, padding: '8px 4px', fontSize: 13, fontWeight: tab === t.k ? 700 : 500,
                background: tab === t.k ? 'var(--card, #fff)' : 'transparent', boxShadow: tab === t.k ? '0 1px 3px rgba(0,0,0,.12)' : 'none' }}>
              {t.k} · {t.k === 'A' ? 'Control' : t.k === 'B' ? 'Llamada' : '+ Oferta'}
            </button>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid var(--border)', marginBottom: 10 }}>
          {tabs.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} className="btn btn-ghost"
              style={{ borderRadius: 0, borderBottom: tab === t.k ? '2px solid var(--foreground)' : '2px solid transparent', fontWeight: tab === t.k ? 700 : 500 }}>
              {t.l}
            </button>
          ))}
        </div>
      )}
      <p style={{ fontSize: 13, margin: '0 0 12px' }}>{tabActual?.d}</p>

      {tab !== 'A' && (
        <details style={{ margin: '0 0 14px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Guion de la llamada</summary>
          <ol style={{ margin: '8px 0 0 18px', lineHeight: 1.6 }}>
            <li>"Hola [nombre], soy Natalie de Neocoop. ¿Tenés un minuto?"</li>
            <li>"Te llamo por tu cuota, que está pendiente desde hace unos días. ¿Cómo estás, pasó algo?" (escuchar)</li>
            {tab === 'C'
              ? <li>"Queremos ayudarte a ponerte al día: si pagás la cuota completa de [monto sin recargo] a más tardar el [fecha], te perdonamos el recargo por mora de [recargo]."</li>
              : <li>"¿Qué día podés pagarla?" (anotar la fecha como promesa)</li>}
            <li>"Podés pagar desde la app con tu ahorro, o depositar y reportarlo en la app con la foto del comprobante."</li>
            <li>"Te mando por WhatsApp el resumen." (botón WhatsApp) · Registrar el resultado.</li>
          </ol>
          <p style={{ margin: '8px 0 0', color: 'var(--muted-foreground)' }}>
            No ofrecer otra cosa (prórroga, abonos, descuento de capital). Si la persona tiene una dificultad seria, anotarla y pasarla a Hugo.
          </p>
        </details>
      )}

      {loading ? <p>Cargando…</p> : isMobile ? (
        /* Celular: una tarjeta por crédito, datos clave arriba y botones grandes abajo */
        <div style={{ display: 'grid', gap: 10 }}>
          {lista.map((r) => {
            const e = ESTADO[r.estado]
            return (
              <div key={r.credito_id} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--card)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <a href={`/neocore/creditos/${r.credito_id}`} style={{ fontWeight: 700, fontSize: 14.5, lineHeight: 1.25 }}>{r.nombre}</a>
                    <div style={{ color: 'var(--muted-foreground)', fontSize: 12 }}>#{r.no_asociado} · {r.telefono ?? 'sin teléfono'}</div>
                  </div>
                  <span style={{ background: e.bg, color: e.fg, padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{e.l}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, margin: '10px 0', fontSize: 12 }}>
                  <div><div style={{ color: 'var(--muted-foreground)' }}>Días</div><b style={{ fontSize: 15 }}>{r.dias_hoy}</b><span style={{ color: 'var(--muted-foreground)' }}> ({r.dias_inicio})</span></div>
                  <div><div style={{ color: 'var(--muted-foreground)' }}>Debe hoy</div><b style={{ fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{fmtQ(r.vencido_hoy)}</b></div>
                  <div><div style={{ color: 'var(--muted-foreground)' }}>Recargo</div><b style={{ fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>{fmtQ(r.mora_hoy)}</b></div>
                </div>
                <div style={{ fontSize: 12.5, marginBottom: 8 }}>
                  {ultimoContacto(r)}
                  {tab === 'C' && <div style={{ color: 'var(--muted-foreground)' }}>Límite oferta: {r.fecha_limite_oferta ?? 'al primer contacto'}</div>}
                </div>
                {acciones(r, true)}
                <ReferenciasCobranza creditoId={r.credito_id} nombreAsociado={r.nombre} plegable />
              </div>
            )
          })}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: 8 }}>Asociado</th>
                <th style={{ padding: 8 }} title="Días hábiles de atraso hoy (entre paréntesis, al inicio del test)">Días</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Debe hoy</th>
                <th style={{ padding: 8, textAlign: 'right' }}>Recargo</th>
                <th style={{ padding: 8 }}>Estado</th>
                <th style={{ padding: 8 }}>Último contacto</th>
                {tab === 'C' && <th style={{ padding: 8 }}>Límite oferta</th>}
                <th style={{ padding: 8 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((r) => {
                const e = ESTADO[r.estado]
                return (
                  <tr key={r.credito_id} style={{ borderBottom: '1px solid var(--border)', verticalAlign: 'top' }}>
                    <td style={{ padding: 8 }}>
                      <a href={`/neocore/creditos/${r.credito_id}`} style={{ fontWeight: 600 }}>{r.nombre}</a>
                      <div style={{ color: 'var(--muted-foreground)', fontSize: 12 }}>#{r.no_asociado} · {r.telefono ?? 'sin teléfono'}</div>
                    </td>
                    <td style={{ padding: 8 }}>{r.dias_hoy}<span style={{ color: 'var(--muted-foreground)', fontSize: 12 }}> ({r.dias_inicio})</span></td>
                    <td style={{ padding: 8, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtQ(r.vencido_hoy)}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtQ(r.mora_hoy)}</td>
                    <td style={{ padding: 8 }}>
                      <span style={{ background: e.bg, color: e.fg, padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{e.l}</span>
                    </td>
                    <td style={{ padding: 8, fontSize: 12.5 }}>{ultimoContacto(r)}</td>
                    {tab === 'C' && <td style={{ padding: 8 }}>{r.fecha_limite_oferta ?? 'al primer contacto'}</td>}
                    <td style={{ padding: 8, whiteSpace: 'nowrap' }}>{acciones(r, false)}<div style={{ whiteSpace: 'normal', maxWidth: 320 }}><ReferenciasCobranza creditoId={r.credito_id} nombreAsociado={r.nombre} plegable /></div></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
